import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Sparkles, Phone } from "lucide-react";
import { PHONE_LINK } from "../lib/data";
import { Logo } from "./Logo";

const LINKS = [
  { label: "Courses", href: "#courses" },
  { label: "Who It's For", href: "#audiences" },
  { label: "Outcomes", href: "#outcomes" },
  { label: "Founder", href: "#founder" },
  { label: "Brand Owners", href: "#brand-owners" },
];

export const Nav = ({ onQuiz, scrollTo }) => {
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 26, mass: 0.4 });

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-nav"
    >
      <motion.div
        style={{ scaleX }}
        className="absolute inset-x-0 top-0 h-[2.5px] origin-left bg-gradient-to-r from-leaf via-champagne to-gold"
        data-testid="scroll-progress-bar"
      />
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16 py-4">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("#top")}
          className="text-left leading-none"
        >
          <Logo />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => scrollTo(l.href)}
              className="group relative text-sm text-neutral-400 hover:text-mint transition-colors duration-300"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gradient-to-r from-leaf to-gold transition-all duration-400 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="nav-phone-link"
            href={PHONE_LINK}
            className="hidden md:flex items-center gap-2 text-sm text-neutral-400 hover:text-mint transition-colors"
          >
            <Phone size={14} /> +91 78509 11096
          </a>
          <motion.button
            data-testid="nav-quiz-button"
            onClick={onQuiz}
            whileHover={{ scale: 1.06 }}
            whileTap={{ scale: 0.95 }}
            className="btn-shine inline-flex items-center gap-2 rounded-full bg-leaf px-5 py-2.5 text-xs sm:text-sm font-semibold text-void shadow-[0_8px_28px_-8px_rgba(232,193,90,0.6)] hover:bg-mint transition-colors"
          >
            <Sparkles size={14} /> Free Assessment
          </motion.button>
        </div>
      </div>
    </motion.header>
  );
};
